import NeptuneElement from '../../NeptuneElement.js';

const BREADCRUMB_LABEL_MAX_LENGTH = 15;

class Breadcrumb extends NeptuneElement {

    templateMarkup = `
        <nav aria-label="Breadcrumb">
            <ul>
                <slot></slot>
            </ul>
        </nav>
    `;

    componentCssUrl = '/src/components/navigation/breadcrumb/breadcrumb.css';

    #breadcrumbHistory;
    #breadcrumbListElement;
    #overflowButtonElement;
    #overflowMenuElement;
    #overflowMenuAttributeObserver;

    constructor() {
        super();

        this.#breadcrumbHistory = [];
        this.#breadcrumbListElement = null;
        this.#overflowButtonElement = null;
        this.#overflowMenuElement = null;
        this.#overflowMenuAttributeObserver = null;

        this.handleBreadcrumbClick = this.handleBreadcrumbClick.bind(this);
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(Breadcrumb.observedAttributes);
    }

    async disconnectedCallback() {
        this.#disconnectOverflowMenuObserver();
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['history'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        if ('history' !== name) {
            return;
        }

        this.#breadcrumbHistory = this.#parseHistoryAttribute(newValue);
        this.#renderBreadcrumb();
    }

    async setEventListeners() {
        if (!this.isRendered) {
            return;
        }

        this.#breadcrumbListElement = this.shadowRoot?.querySelector('ul') || null;
        this.shadowRoot?.addEventListener('click', this.handleBreadcrumbClick);
    }

    async removeEventListeners() {
        this.shadowRoot?.removeEventListener('click', this.handleBreadcrumbClick);
        this.#disconnectOverflowMenuObserver();

        this.#breadcrumbListElement = null;
        this.#overflowButtonElement = null;
        this.#overflowMenuElement = null;
    }

    /**
     * Renders the breadcrumb list based on the current `history` value.
     */
    #renderBreadcrumb() {
        if (!this.isRendered) {
            return;
        }

        const breadcrumbListElement = this.#breadcrumbListElement || this.shadowRoot?.querySelector('ul');
        if (!breadcrumbListElement) {
            return;
        }

        this.#breadcrumbListElement = breadcrumbListElement;
        this.#disconnectOverflowMenuObserver();

        breadcrumbListElement.innerHTML = '';

        if (!Array.isArray(this.#breadcrumbHistory) || 0 === this.#breadcrumbHistory.length) {
            const slot = document.createElement('slot');
            breadcrumbListElement.appendChild(slot);
            return;
        }

        const mobileParentListItem = this.#createMobileParentListItem(this.#breadcrumbHistory);
        if (mobileParentListItem) {
            breadcrumbListElement.appendChild(mobileParentListItem);
        }

        const renderModel = this.#buildRenderModel(this.#breadcrumbHistory);

        breadcrumbListElement.appendChild(this.#createBreadcrumbListItem(renderModel.first, false));

        if (renderModel.shouldCollapse) {
            breadcrumbListElement.appendChild(this.#createOverflowMenuListItem(renderModel.hidden));
        }

        renderModel.tail.forEach((item, index) => {
            const isCurrentPage = index === renderModel.tail.length - 1;
            breadcrumbListElement.appendChild(this.#createBreadcrumbListItem(item, isCurrentPage));
        });

        this.#cacheOverflowDomReferences();
    }

    handleBreadcrumbClick(event) {
        const overflowButtonElement = this.#findElementInComposedPath(event, (element) => {
            return element?.getAttribute?.('data-role') === 'overflow-trigger';
        });
        if (overflowButtonElement) {
            const overflowMenuElement = overflowButtonElement.nextElementSibling;
            if (overflowMenuElement && 'NEPTUNE-MENU' === overflowMenuElement.tagName) {
                overflowMenuElement.toggleAttribute('open');
                overflowButtonElement.setAttribute(
                    'aria-expanded',
                    overflowMenuElement.hasAttribute('open') ? 'true' : 'false'
                );
            }
            return;
        }

        const overflowMenuLinkElement = event.target?.closest?.('neptune-menu[data-role="overflow-menu"] a');
        if (!overflowMenuLinkElement) {
            return;
        }

        const overflowMenuElement = overflowMenuLinkElement.closest('neptune-menu');
        if (!overflowMenuElement) {
            return;
        }

        const overflowTriggerElement = overflowMenuElement.previousElementSibling;
        overflowMenuElement.removeAttribute('open');
        if (overflowTriggerElement) {
            overflowTriggerElement.setAttribute('aria-expanded', 'false');
        }
    }

    #findElementInComposedPath(event, predicate) {
        const path = event?.composedPath?.() || [];
        for (let index = 0; index < path.length; index++) {
            const element = path[index];
            if (predicate(element)) {
                return element;
            }
        }
        return null;
    }

    #parseHistoryAttribute(value) {
        if (!value) {
            return [];
        }

        try {
            const parsed = JSON.parse(value);
            if (!Array.isArray(parsed)) {
                return [];
            }

            return parsed
                .filter((item) => item && typeof item === 'object')
                .map((item) => ({
                    name: String(item.name ?? '').trim(),
                    route: String(item.route ?? '').trim()
                }))
                .filter((item) => item.name.length > 0);
        } catch (error) {
            console.error('Invalid history JSON:', error);
            return [];
        }
    }

    #buildRenderModel(history) {
        const totalItemCount = history.length;
        const shouldCollapse = totalItemCount > 4;

        if (!shouldCollapse) {
            return {
                shouldCollapse: false,
                first: history[0],
                tail: history.slice(1),
                hidden: []
            };
        }

        return {
            shouldCollapse: true,
            first: history[0],
            tail: history.slice(totalItemCount - 2),
            hidden: history.slice(1, totalItemCount - 2)
        };
    }

    #createMobileParentListItem(history) {
        if (!Array.isArray(history) || history.length < 2) {
            return null;
        }

        const parentItem = history[history.length - 2];
        const listItemElement = document.createElement('li');
        listItemElement.setAttribute('data-role', 'mobile-parent');

        const linkElement = document.createElement('a');
        linkElement.classList.add('font-size-lg', 'font-weight-regular');
        linkElement.href = parentItem.route || '#';
        this.#applyLabelContent(linkElement, parentItem.name);
        linkElement.setAttribute('aria-label', `Back to ${parentItem.name}`);

        listItemElement.appendChild(linkElement);
        return listItemElement;
    }

    #getFormattedLabel(name) {
        const label = String(name ?? '');
        const isTruncated = label.length > BREADCRUMB_LABEL_MAX_LENGTH;

        return {
            fullText: label,
            displayText: isTruncated
                ? `${label.slice(0, BREADCRUMB_LABEL_MAX_LENGTH).trimEnd()}...`
                : label,
            isTruncated
        };
    }

    #applyLabelContent(element, name) {
        const { fullText, displayText, isTruncated } = this.#getFormattedLabel(name);

        element.textContent = displayText;
        if (isTruncated) {
            element.setAttribute('title', fullText);
            element.setAttribute('aria-label', fullText);
            return;
        }

        element.removeAttribute('title');
        element.removeAttribute('aria-label');
    }

    #createBreadcrumbListItem(item, isCurrentPage) {
        const listItemElement = document.createElement('li');

        if (isCurrentPage) {
            const currentPageTextElement = document.createElement('span');
            currentPageTextElement.classList.add('font-size-lg', 'font-weight-regular');
            currentPageTextElement.setAttribute('aria-current', 'page');
            this.#applyLabelContent(currentPageTextElement, item.name);
            listItemElement.appendChild(currentPageTextElement);
            return listItemElement;
        }

        const linkElement = document.createElement('a');
        linkElement.classList.add('font-size-lg' , 'font-weight-regular');
        linkElement.href = item.route || '#';
        this.#applyLabelContent(linkElement, item.name);
        listItemElement.appendChild(linkElement);
        return listItemElement;
    }

    #createOverflowMenuListItem(hiddenItems) {
        const overflowListItemElement = document.createElement('li');
        overflowListItemElement.setAttribute('data-role', 'overflow');

        const overflowButtonElement = document.createElement('neptune-button');
        overflowButtonElement.setAttribute('type', 'tertiary');
        overflowButtonElement.setAttribute('size', 'small');
        overflowButtonElement.className = 'overflow-trigger';
        overflowButtonElement.textContent = '. . .';
        overflowButtonElement.setAttribute('data-role', 'overflow-trigger');
        overflowButtonElement.setAttribute('aria-label', 'Show breadcrumb history');
        overflowButtonElement.setAttribute('aria-haspopup', 'menu');
        overflowButtonElement.setAttribute('aria-expanded', 'false');
        overflowButtonElement.style.setProperty('anchor-name', '--neptune-breadcrumb-overflow-anchor');

        const overflowMenuElement = document.createElement('neptune-menu');
        overflowMenuElement.setAttribute('data-role', 'overflow-menu');
        overflowMenuElement.style.setProperty('position-anchor', '--neptune-breadcrumb-overflow-anchor');

        (Array.isArray(hiddenItems) ? hiddenItems : []).forEach((crumb) => {
            const overflowMenuItemElement = document.createElement('neptune-menu-item');
            const linkElement = document.createElement('a');
            linkElement.classList.add('font-size-lg' , 'font-weight-regular');
            linkElement.href = crumb.route || '#';
            this.#applyLabelContent(linkElement, crumb.name);
            overflowMenuItemElement.appendChild(linkElement);
            overflowMenuElement.appendChild(overflowMenuItemElement);
        });

        overflowListItemElement.appendChild(overflowButtonElement);
        overflowListItemElement.appendChild(overflowMenuElement);
        return overflowListItemElement;
    }

    #cacheOverflowDomReferences() {
        this.#overflowButtonElement = this.shadowRoot?.querySelector('[data-role="overflow-trigger"]') || null;
        this.#overflowMenuElement = this.shadowRoot?.querySelector('neptune-menu[data-role="overflow-menu"]') || null;

        if (!this.#overflowButtonElement || !this.#overflowMenuElement) {
            return;
        }

        this.#overflowMenuAttributeObserver = new MutationObserver(() => {
            this.#overflowButtonElement?.setAttribute(
                'aria-expanded',
                this.#overflowMenuElement?.hasAttribute('open') ? 'true' : 'false'
            );
        });

        this.#overflowMenuAttributeObserver.observe(this.#overflowMenuElement, {
            attributes: true,
            attributeFilter: ['open']
        });
    }

    #disconnectOverflowMenuObserver() {
        if (!this.#overflowMenuAttributeObserver) {
            return;
        }

        this.#overflowMenuAttributeObserver.disconnect();
        this.#overflowMenuAttributeObserver = null;
    }
}

customElements.define('neptune-breadcrumb', Breadcrumb);

export default Breadcrumb;